import { stripHtmlToText } from './richText';
import { uploadFileToS3 } from './s3ClientUpload';
import {
  DOCUMENT_PARENT_TABLES,
  DOCUMENT_SOURCE_SLUGS,
  FORM_UPLOAD_CATEGORY
} from '../constants/documentSources';
import {
  submissionAnswersToFormData,
  isQuestionSnapshotAnswers,
  extractUserAnswers as extractStoredUserAnswers
} from './submissionAnswers';

export function createPendingFileRef(file) {
  const localKey =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return {
    _pending: true,
    localKey,
    name: file.name,
    size: file.size,
    type: file.type || null
  };
}

export function getFileRefDocumentId(item) {
  if (!item || typeof item !== 'object') return null;
  return item.documentId || item.document_id || null;
}

export function normalizeStoredFileRef(item) {
  if (!item || typeof item !== 'object') return null;
  const documentId = getFileRefDocumentId(item);
  if (!documentId && !isPendingFormFileRef(item)) {
    if (item.name) {
      return {
        name: item.name,
        size: item.size,
        type: item.type || null
      };
    }
    return null;
  }
  if (isPendingFormFileRef(item)) return { ...item };
  return {
    documentId,
    fileName: item.fileName || item.file_name || item.name || 'File',
    url: item.url || null,
    size: item.size ?? item.file_size_bytes ?? null,
    type: item.type || item.mime_type || null
  };
}

export function isStoredFormFileRef(value) {
  return Boolean(getFileRefDocumentId(value));
}

export function isPendingFormFileRef(value) {
  return Boolean(value && typeof value === 'object' && value._pending && value.localKey);
}

export function normalizeFormFileAnswerList(value, allowMultiple) {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  const normalized = list
    .map((item) => normalizeStoredFileRef(item))
    .filter(Boolean);
  if (!allowMultiple && normalized.length > 1) return [normalized[0]];
  return normalized;
}

export function isFormFileAnswerEmpty(value, allowMultiple = true) {
  const list = normalizeFormFileAnswerList(value, allowMultiple);
  if (list.length === 0) return true;
  return list.every((item) => {
    if (!item || typeof item !== 'object') return true;
    if (isStoredFormFileRef(item)) return false;
    if (isPendingFormFileRef(item)) return false;
    return true;
  });
}

export function getFormFileUploadQuestions(form) {
  return (form?.questions || []).filter((q) => q.type === 'file-upload' && q.question_id);
}

export function hasPendingFormFileUploads(form, answers, pendingBlobs = {}) {
  for (const question of getFormFileUploadQuestions(form)) {
    const allowMultiple = question.allowMultiple !== false;
    const list = normalizeFormFileAnswerList(answers[question.question_id], allowMultiple);
    for (const item of list) {
      if (isPendingFormFileRef(item) && pendingBlobs[item.localKey]) {
        return true;
      }
    }
  }
  return false;
}

export function stripFileUploadAnswers(form, answers) {
  const next = { ...(answers || {}) };
  for (const question of getFormFileUploadQuestions(form)) {
    delete next[question.question_id];
  }
  return next;
}

/** @deprecated Use stripFileUploadAnswers — file uploads live in documents, not answers. */
export function stripPendingFileAnswers(form, answers) {
  return stripFileUploadAnswers(form, answers);
}

async function createFormUploadDocument({
  fetchFn,
  submissionId,
  ugpId,
  form,
  question,
  upload,
  replacesDocumentId = null
}) {
  const questionLabel = stripHtmlToText(question.label) || 'Upload';
  const title = `${form.title || 'Form'} — ${questionLabel}`.slice(0, 240);
  const body = {
    source_slug: DOCUMENT_SOURCE_SLUGS.FORM_UPLOADS,
    parent_table: DOCUMENT_PARENT_TABLES.FORM_SUBMISSIONS,
    parent_id: submissionId,
    user_groups_periods_id: ugpId,
    category: FORM_UPLOAD_CATEGORY,
    title,
    description: questionLabel,
    url: upload.url,
    storage_kind: 's3',
    mime_type: upload.mimeType,
    file_name: upload.fileName,
    file_size_bytes: upload.fileSizeBytes,
    metadata: {
      form_id: form.id,
      form_title: form.title || null,
      question_id: question.question_id,
      question_label: questionLabel
    },
    ...(replacesDocumentId ? { replaces_document_id: replacesDocumentId } : {})
  };

  const res = await fetchFn('/api/documents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await res.json();
  if (!data.success) {
    throw new Error(data.message || 'Failed to create form upload document');
  }
  return data.data;
}

/**
 * Upload pending files to S3 and create document rows linked to the submission.
 * File uploads are stored in documents (parent_id + metadata.question_id), not in answers.
 */
export async function uploadPendingFormFiles({
  form,
  answers,
  submissionId,
  ugpId,
  pendingBlobs = {},
  fetchFn = fetch,
  generateId
}) {
  if (!submissionId) {
    throw new Error('Submission id is required before uploading form files');
  }
  if (!ugpId) {
    throw new Error('Enrollment context (user_groups_periods_id) is required to store form uploads');
  }

  const created = [];
  const uploadQuestions = getFormFileUploadQuestions(form);
  if (uploadQuestions.length === 0) return created;

  for (const question of uploadQuestions) {
    const qid = question.question_id;
    if (answers[qid] === undefined) continue;
    const allowMultiple = question.allowMultiple !== false;
    const list = normalizeFormFileAnswerList(answers[qid], allowMultiple);
    const pendingItems = list.filter((item) => isPendingFormFileRef(item));
    if (pendingItems.length === 0) continue;

    for (const item of pendingItems) {
      const file = pendingBlobs[item.localKey];
      if (!file) {
        throw new Error(`Missing file data for "${item.name || 'upload'}". Please re-select the file.`);
      }

      const upload = await uploadFileToS3(file, {
        fileLocation: 'documents',
        customFilename: generateId ? generateId() : undefined
      });

      const doc = await createFormUploadDocument({
        fetchFn,
        submissionId,
        ugpId,
        form,
        question,
        upload,
        replacesDocumentId: item.replacesDocumentId || null
      });
      created.push(doc);
    }
  }

  return created;
}

/** @deprecated Use uploadPendingFormFiles — answers are not updated with document refs. */
export async function processFormFileAnswers(opts) {
  await uploadPendingFormFiles(opts);
  const hydrated = await hydrateFormSubmissionFileAnswers(
    opts.form,
    opts.answers,
    opts.submissionId,
    opts.fetchFn
  );
  return hydrated.answers;
}

export async function fetchFormSubmissionDocuments(submissionId, fetchFn = fetch) {
  if (!submissionId) return [];
  const params = new URLSearchParams({
    parent_table: DOCUMENT_PARENT_TABLES.FORM_SUBMISSIONS,
    parent_id: String(submissionId),
    source_slug: DOCUMENT_SOURCE_SLUGS.FORM_UPLOADS
  });
  const res = await fetchFn(`/api/documents?${params.toString()}`);
  const data = await res.json();
  if (!data.success) return [];
  return data.data || [];
}

export function buildDocumentUrlMap(documents) {
  const map = {};
  (documents || []).forEach((doc) => {
    if (doc?.id) map[doc.id] = doc.url || null;
  });
  return map;
}

/** Merge submission answers with form_upload documents (urls + missing refs). */
export function hydrateFormSubmissionFiles(form, answers, documents) {
  const base = submissionAnswersToFormData(answers);
  const next = { ...base };
  const docs = Array.isArray(documents) ? documents : [];
  const urlById = buildDocumentUrlMap(docs);
  const docsByQuestionId = new Map();

  docs.forEach((doc) => {
    const qid = doc?.metadata?.question_id;
    if (!qid) return;
    const ref = {
      documentId: doc.id,
      fileName: doc.file_name || doc.title || 'File',
      url: doc.url || null,
      size: doc.file_size_bytes ?? null,
      type: doc.mime_type || null
    };
    if (!docsByQuestionId.has(qid)) docsByQuestionId.set(qid, []);
    docsByQuestionId.get(qid).push(ref);
  });

  for (const question of getFormFileUploadQuestions(form)) {
    const qid = question.question_id;
    const allowMultiple = question.allowMultiple !== false;
    const docRefs = docsByQuestionId.get(qid) || [];
    const existing = normalizeFormFileAnswerList(next[qid], allowMultiple).filter(
      (item) => isStoredFormFileRef(item) || isPendingFormFileRef(item)
    );

    const enrichRef = (item) => {
      if (isPendingFormFileRef(item)) return item;
      const documentId = getFileRefDocumentId(item);
      return {
        ...item,
        documentId,
        fileName: item.fileName || item.name || 'File',
        url: item.url || (documentId ? urlById[documentId] : null) || null
      };
    };

    if (docRefs.length > 0) {
      next[qid] = allowMultiple ? docRefs : docRefs[0];
      continue;
    }

    if (existing.length > 0) {
      const enriched = existing.map(enrichRef);
      next[qid] = allowMultiple ? enriched : enriched[0];
    }
  }

  return next;
}

function applyHydratedResponsesToStored(stored, hydratedFlat) {
  if (!isQuestionSnapshotAnswers(stored)) return hydratedFlat;
  const responses = extractStoredUserAnswers(hydratedFlat);
  return {
    ...stored,
    questions: stored.questions.map((row) => ({
      ...row,
      response:
        responses[row.question_id] !== undefined ? responses[row.question_id] : row.response
    }))
  };
}

export async function hydrateFormSubmissionFileAnswers(form, answers, submissionId, fetchFn = fetch) {
  const docs = submissionId ? await fetchFormSubmissionDocuments(submissionId, fetchFn) : [];
  const hydratedFlat = hydrateFormSubmissionFiles(form, answers, docs);
  return {
    answers: isQuestionSnapshotAnswers(answers)
      ? applyHydratedResponsesToStored(answers, hydratedFlat)
      : hydratedFlat,
    formData: hydratedFlat,
    documents: docs,
    documentUrlById: buildDocumentUrlMap(docs)
  };
}

export function releasePendingBlobKeys(pendingBlobs, keys = []) {
  keys.forEach((key) => {
    delete pendingBlobs[key];
  });
}

export function clearFormPendingBlobs(pendingBlobsByFormId, formId) {
  if (pendingBlobsByFormId[formId]) {
    pendingBlobsByFormId[formId] = {};
  }
}
